/**
 * Contact page form (sections/contact-form.liquid).
 *
 * Posts directly to the backend instead of Shopify's native `{% form
 * 'contact' %}` handler, because the "incidencia" (issue) reason lets the
 * customer attach photos of the product and the packaging -- Shopify's own
 * contact form has no attachment support. Every submission (general
 * question, order status, or an issue with photos) goes through the same
 * POST /support/messages endpoint; incident photos upload immediately on
 * selection to POST /uploads?purpose=support (same endpoint the product-page
 * widget uses for pet photos, but with purpose=support so the backend skips
 * its pet-detection check -- see app/routers/uploads.py).
 *
 * User-facing strings come from a JSON blob emitted by the section
 * ([data-ronys-contact-i18n]), same pattern as photo-upload-widget.js.
 */
(function () {
  const BACKEND_UPLOAD_URL = "https://ronys-backend-144174532283.us-central1.run.app/uploads";
  const BACKEND_SUPPORT_MESSAGE_URL = "https://ronys-backend-144174532283.us-central1.run.app/support/messages";
  const MAX_PHOTOS_PER_SIDE = 3;

  function readI18n(root) {
    const script = root.querySelector("[data-ronys-contact-i18n]");
    if (!script) return {};
    try {
      return JSON.parse(script.textContent);
    } catch (err) {
      return {};
    }
  }

  function initPhotoZone(zoneEl, t) {
    const input = zoneEl.querySelector("[data-photo-input]");
    const addButton = zoneEl.querySelector("[data-photo-add]");
    const thumbsEl = zoneEl.querySelector("[data-photo-thumbs]");
    const statusEl = zoneEl.querySelector("[data-photo-status]");
    const idsField = zoneEl.querySelector("[data-photo-ids-field]");
    // Sparse, index-aligned with the thumbnail it belongs to: null while
    // uploading or after a failed/removed upload, a real upload_id once done.
    const uploadIds = [];

    function syncField() {
      idsField.value = uploadIds.filter(Boolean).join(",");
    }

    function updateAddButtonState() {
      const atLimit = uploadIds.length >= MAX_PHOTOS_PER_SIDE;
      addButton.disabled = atLimit;
      statusEl.textContent = atLimit ? t.maxPhotosReached || "" : "";
    }

    function reset() {
      uploadIds.length = 0;
      thumbsEl.innerHTML = "";
      syncField();
      updateAddButtonState();
    }

    addButton.addEventListener("click", () => input.click());

    input.addEventListener("change", () => {
      const files = Array.from(input.files || []);
      input.value = ""; // allow re-selecting the same file after removing it
      files.forEach((file) => {
        if (uploadIds.length >= MAX_PHOTOS_PER_SIDE) return;
        addPhoto(file);
      });
    });

    function addPhoto(file) {
      const index = uploadIds.length;
      uploadIds.push(null);
      updateAddButtonState();

      const thumb = document.createElement("div");
      thumb.className = "ronys-photo-thumb";

      const img = document.createElement("img");
      img.alt = "";
      const reader = new FileReader();
      reader.onload = () => {
        img.src = reader.result;
      };
      reader.readAsDataURL(file);

      const status = document.createElement("span");
      status.className = "ronys-photo-thumb__status";
      status.textContent = t.uploading || "";

      const removeBtn = document.createElement("button");
      removeBtn.type = "button";
      removeBtn.className = "ronys-photo-thumb__remove";
      removeBtn.setAttribute("aria-label", t.removePhoto || "");
      removeBtn.textContent = "×";
      removeBtn.addEventListener("click", () => {
        uploadIds[index] = null;
        syncField();
        thumb.remove();
        updateAddButtonState();
      });

      thumb.append(img, status, removeBtn);
      thumbsEl.appendChild(thumb);

      zoneEl.dispatchEvent(new CustomEvent("ronys:upload-start", { bubbles: true }));

      const formData = new FormData();
      formData.append("file", file);
      formData.append("purpose", "support");

      fetch(BACKEND_UPLOAD_URL, { method: "POST", body: formData })
        .then(async (response) => {
          if (!response.ok) {
            const body = await response.json().catch(() => ({}));
            const detail = body.detail;
            const msg =
              detail === "file_too_large"
                ? t.uploadErrorTooLarge
                : detail === "unsupported_format"
                  ? t.uploadErrorFormat
                  : t.uploadErrorGeneric;
            throw new Error(msg || "");
          }
          return response.json();
        })
        .then(({ upload_id }) => {
          uploadIds[index] = upload_id;
          syncField();
          status.textContent = "";
        })
        .catch((err) => {
          uploadIds[index] = null;
          syncField();
          status.textContent = err.message || t.uploadErrorGeneric || "";
          status.classList.add("ronys-photo-thumb__status--error");
        })
        .finally(() => {
          zoneEl.dispatchEvent(new CustomEvent("ronys:upload-end", { bubbles: true }));
        });
    }

    updateAddButtonState();
    return { reset };
  }

  function initContactForm(form) {
    const t = readI18n(form);
    const root = form.closest("[data-ronys-locale]");
    const reasonSelect = form.querySelector("[data-reason-select]");
    const orderField = form.querySelector("[data-order-field]");
    const issueBlock = form.querySelector("[data-issue-block]");
    const submitButton = form.querySelector("[data-submit-button]");
    const submitLabel = form.querySelector("[data-submit-label]");
    const statusBox = form.querySelector("[data-form-status]");
    const statusIconSuccess = form.querySelector("[data-status-icon-success]");
    const statusIconError = form.querySelector("[data-status-icon-error]");
    const statusText = form.querySelector("[data-status-text]");

    const photoZones = Array.from(form.querySelectorAll("[data-photo-zone]")).map((zoneEl) =>
      initPhotoZone(zoneEl, t)
    );

    function updateReasonVisibility() {
      const reason = reasonSelect.value;
      const showOrder = reason === "order_status" || reason === "issue";
      const showIssue = reason === "issue";
      orderField.hidden = !showOrder;
      issueBlock.hidden = !showIssue;
    }
    reasonSelect.addEventListener("change", updateReasonVisibility);
    updateReasonVisibility();

    let pendingUploads = 0;
    function refreshSubmitAvailability() {
      submitButton.disabled = pendingUploads > 0;
    }
    form.addEventListener("ronys:upload-start", () => {
      pendingUploads += 1;
      refreshSubmitAvailability();
    });
    form.addEventListener("ronys:upload-end", () => {
      pendingUploads = Math.max(0, pendingUploads - 1);
      refreshSubmitAvailability();
    });

    function showStatus(kind, message) {
      statusBox.hidden = false;
      statusBox.classList.remove("form__message--success", "form__message--error");
      statusBox.classList.add(`form__message--${kind}`);
      statusIconSuccess.hidden = kind !== "success";
      statusIconError.hidden = kind !== "error";
      statusText.textContent = message;
      statusBox.focus({ preventScroll: false });
    }

    function setSending(isSending) {
      submitButton.disabled = isSending || pendingUploads > 0;
      submitLabel.textContent = isSending ? t.sending || "" : t.send || "";
    }

    form.addEventListener("submit", async (event) => {
      event.preventDefault();
      if (pendingUploads > 0) return;

      statusBox.hidden = true;
      setSending(true);

      const data = new FormData(form);
      const payload = {
        name: (data.get("name") || "").toString().trim(),
        email: (data.get("email") || "").toString().trim(),
        phone: (data.get("phone") || "").toString().trim() || null,
        order_number: (data.get("order_number") || "").toString().trim() || null,
        reason: data.get("reason"),
        message: (data.get("message") || "").toString().trim(),
        product_photo_ids: (data.get("product_photo_ids") || "")
          .toString()
          .split(",")
          .filter(Boolean),
        packaging_photo_ids: (data.get("packaging_photo_ids") || "")
          .toString()
          .split(",")
          .filter(Boolean),
        honeypot: (data.get("company") || "").toString(),
        locale: (root && root.dataset.ronysLocale) || "",
      };

      try {
        const response = await fetch(BACKEND_SUPPORT_MESSAGE_URL, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });

        if (!response.ok) {
          const message = response.status === 429 ? t.rateLimited : t.submitError;
          throw new Error(message || "");
        }

        showStatus("success", t.postSuccess || "");
        form.reset();
        photoZones.forEach((zone) => zone.reset());
        updateReasonVisibility();
      } catch (err) {
        showStatus("error", err.message || t.submitError || "");
      } finally {
        setSending(false);
      }
    });
  }

  document.querySelectorAll("[data-ronys-contact-form]").forEach(initContactForm);
})();
